/**
 * English — secondary UI locale. Typed as a complete map over the DE key set,
 * so a missing key is a compile error, not a runtime "??".
 *
 * Domain terms stay German in every locale (Baumassenziffer, Ausnützungsziffer,
 * Nachweis, Baueingabe, Gemeinde, BZO) — that is the vocabulary the reader uses
 * in the actual permit file.
 */
import type { TranslationKey } from './de'

export const en: Record<TranslationKey, string> = {
  'app.name': 'Baueingabe-Check-KAi',
  'app.attribution': 'by Velox-KAi',
  'app.tagline': 'Pre-check for Baueingabe dossiers · Canton of Zurich',

  'nav.dossiers': 'Dossiers',
  'nav.report': 'Report',
  'nav.run': 'Check run',

  'lang.label': 'Language',
  'lang.de': 'Deutsch (CH)',
  'lang.en': 'English',
  'lang.fr': 'Français',
  'lang.frPending': 'pending',
  'lang.frBanner': 'The French interface is not translated yet. Showing German (CH).',

  'a11y.skipToContent': 'Skip to content',

  // One title per route (WCAG 2.4.2). The view first, the product second.
  'title.home': 'Overview',
  'title.signIn': 'Sign in',
  'title.invite': 'Invitation',
  'title.workspaceIndex': 'Workspaces',
  'title.workspace': 'Workspace',
  'title.upload': 'Upload dossier',
  'title.facts': 'Review facts',
  'title.dossierAudit': 'Dossier history',
  'title.admin': 'Administration',
  'title.rulebase': 'Gemeinden and rulebase',
  'title.rulebaseIngest': 'Upload BZO',
  'title.rulebaseIngestion': 'BZO ingestion',
  'title.rulebasePack': 'Verify draft',
  'title.run': 'Run',
  'title.report': 'Report',
  'title.reportPrint': 'Report — print form',
  'title.notFound': 'Page not found',

  'common.loading': 'Loading …',
  'common.error': 'Error',
  'common.retry': 'Try again',
  'common.back': 'Back',
  'common.none': '—',
  'common.of': 'of',
  'common.yes': 'yes',
  'common.no': 'no',
  'common.cancel': 'Cancel',

  'start.title': 'Check a demo dossier',
  'start.lead':
    'The Baueingabe-Check flags findings with their source and § — it does not decide and does not confirm completeness. Responsibility stays with the Projektverfasser.',
  'start.dossiersTitle': 'Built-in demo dossiers',
  'start.dossiersLead': 'Every built-in dossier is synthetic — no real personal data.',
  'start.dossiersEmpty': 'No built-in dossiers found.',
  'start.synthetic': 'synthetic',
  'start.startRun': 'Start check run',
  'start.starting': 'Starting …',
  'start.gemeindenTitle': 'Rulebase coverage',
  'start.gemeindenLead':
    'Which packs exist, and how many of their rules are verified against a primary source.',
  'start.gemeindenEmpty': 'No rulebase pack found.',

  'coverage.zones': 'Zones',
  'coverage.rules': 'Effective rules',
  'coverage.verified': 'verified',
  'coverage.unverified': 'unverified',
  'coverage.placeholder': 'placeholder',
  'coverage.regime': 'Regime',
  'coverage.sources': 'Sources',
  'coverage.mode.verified': 'verified',
  'coverage.mode.indicative': 'indicative',

  'run.title': 'Check run',
  'run.lead': 'The stages below are the stages the backend actually reports.',
  'run.id': 'Run ID',
  'run.dossier': 'Dossier',
  'run.stagesTitle': 'Stages',
  'run.openReport': 'Open report',
  'run.failedTitle': 'The run failed',
  'run.pollError': 'Could not fetch the run status.',
  'run.statusLabel': 'Status',
  'run.status.queued': 'queued',
  'run.status.running': 'running',
  'run.status.succeeded': 'finished',
  'run.status.failed': 'failed',
  'run.elapsed': 'Running for {minutes} min',
  'run.longRunning': 'This run is taking unusually long. If the server was restarted in the meantime, it marks the run as failed on start-up; the dossier can then be checked again.',
  'stage.pending': 'pending',
  'stage.running': 'running',
  'stage.done': 'done',
  'stage.skipped': 'skipped',
  'stage.failed': 'failed',

  'report.title': 'Report',
  'report.gemeinde': 'Gemeinde',
  'report.zone': 'Zone',
  'report.regime': 'Regime',
  'report.case': 'Case',
  'report.mode': 'Mode',
  'report.ampelTitle': 'Traffic lights',
  'report.ampelLead': 'Counts the hard findings only.',
  'report.findingsTitle': 'Findings',
  'report.findingsLead':
    'Only from verified rules on a verified zone. Every finding names the rule, the § and the computed values.',
  'report.findingsEmpty': 'No hard findings.',
  'report.indicativeTitle': 'Indicative — not verified',
  'report.indicativeLead':
    'This output comes from rules or zones that are not yet verified against the BZO. It is not a traffic-light finding and carries no signal colour.',
  'report.indicativeBadge': 'Indicative · not verified',
  'report.indicativeEmpty': 'No indicative output.',
  'report.indicativeReason': 'Reason',
  'report.modeIndicativeBanner':
    'Draft — not yet verified against the BZO. In this mode there are no hard findings, only indicative output.',
  'report.hinweiseTitle': 'Notes',
  'report.hinweiseLead': 'Judgement-based aspects and indicators — not findings.',
  'report.hinweisBadge': 'Note',
  'report.confidence': 'Confidence',
  'report.nachweiseTitle': 'Vorbehaltene Nachweise',
  'report.nachweiseLead': 'Due later — not a reason to hold back the submission.',
  'report.nachweisDue': 'due',
  'report.nachweisOpen': 'open',
  'report.nachweisDone': 'provided',
  'report.skippedTitle': 'Manual · to be checked',
  'report.skippedLead':
    'These rules did not run. They are neither met nor violated — the reason is stated on every row.',
  'report.skippedReason': 'Reason',
  'report.skippedGroup.missingInput': 'Input value missing',
  'report.skippedGroup.missingInputLead':
    'The rule would compute — the value is not in the dossier. Add it in the facts review and the check runs.',
  'report.skippedGroup.missingGis': 'GIS value missing',
  'report.skippedGroup.missingGisLead':
    'Not checkable without a GIS connection for the parcel. Check these rules manually against the official geodata.',
  'report.skippedGroup.notComputable': 'Rulebase does not cover the case',
  'report.skippedGroup.notComputableLead':
    'The pack describes the rule, but the check could not be computed from it. These rules need a full manual check.',
  'report.skippedGroup.unverified': 'Rule not verified yet',
  'report.skippedGroup.unverifiedLead':
    'The rule is not confirmed against the primary source in the pack and therefore cannot produce a hard finding.',
  'report.skippedGroup.other': 'Other reasons',
  'report.skippedGroup.otherLead':
    'Reasons that fit none of the groups — please read them one by one.',
  'report.skippedGroup.notApplicable': 'Not applicable in this zone',
  'report.skippedGroup.notApplicableLead':
    'No defect in the project: these rules apply to a different regime, or to a zone parameter this zone does not carry.',
  'report.grenzenTitle': 'Limits',
  'report.grenzenLead': 'What this report does not check.',
  'report.rule': 'Rule',
  'report.source': 'Source',
  'report.pack': 'Pack',
  'report.computed': 'Computed',
  'report.missing': 'Missing input values',
  'report.newRun': 'Check another dossier',
  'report.loadError': 'The report could not be loaded.',

  // --- M3: finding card, the honest sections, the source register ----------
  'report.asOf': 'as of',
  'report.actionTitle': 'What to do about it',
  'report.skippedEmpty':
    'No rule was skipped. What was checked is still only what the rulebase pack carries as a verified rule — this line says nothing about topics that have no rule at all.',
  'report.nachweiseEmpty':
    'The rulebase pack carries no vorbehaltene Nachweise for this zone. That does not mean the building authority will ask for none — it means none are recorded here.',
  'report.hinweiseEmpty': 'No indicators for this run.',
  'report.grenzenEmpty':
    'No limits were returned for this run. The report remains a set of flagged findings and says nothing about completeness.',
  'report.sourcesTitle': 'Sources and version',
  'report.sourcesLead':
    'The documents this Gemeinde’s rulebase pack relies on, with the version the pack records. A single rule states its version only when the rule itself carries one — it is never guessed.',
  'report.sourcesEmpty': 'No source documents are registered for this pack.',
  'report.sourceNoVersion': 'no version recorded in the pack',
  'report.printView': 'Print view',
  'report.pdfDownload': 'Download PDF',

  'confidence.deterministic': 'deterministic',
  'confidence.retrieval': 'retrieval',
  'confidence.indicator': 'Indicator',
  'confidence.level.high': 'high',
  'confidence.level.medium': 'medium',
  'confidence.level.low': 'low',
  'confidence.deterministic.meaning':
    'Code compared the input value against the threshold of the cited provision. No model took part in the arithmetic.',
  'confidence.retrieval.meaning':
    'From a rulebase pack that is not verified against the primary source at this point.',
  'confidence.indicator.meaning':
    'An experience value with no legal consequence — not a finding and not a traffic light.',

  'action.green':
    'Nothing to do. The rule holds with the values entered; change a value and the finding changes.',
  'action.missingArtifacts': 'Supply the missing document: {items}. Then run the check again.',
  'action.missingFormFields':
    'Enter these form fields as a number: {items}. Text where a number belongs counts as empty.',
  'action.inconsistent':
    'The form and the GIS query disagree. Establish which value is right and correct the Baugesuch form accordingly.',
  'action.gisFlag':
    'The GIS query reports an attribute that needs its own clarification for this parcel. Involve the responsible office.',
  'action.limitNotMet':
    'The input value does not meet the threshold of the cited provision. Adjust the project, or justify and document the deviation in the Baugesuch.',
  'action.generic': 'Check the finding against the cited provision and answer it in the Baugesuch.',
  'action.indicative':
    'This rule is not verified against the primary source yet. Read the cited provision yourself before drawing any consequence — there is no finding here.',

  // --- M3: "How do I read this report?" ------------------------------------
  'explainer.title': 'How do I read this report?',
  'explainer.lead':
    'What the traffic lights state, where a number comes from, why a rule did not run — and where the responsibility stays.',
  'explainer.show': 'Show the explanation',
  'explainer.hide': 'Hide the explanation',
  'explainer.lights.title': 'The three traffic lights',
  'explainer.lights.body':
    'Red: a verified rule is not met with the values at hand. Amber: the rule applies, but an input value is missing or open — the point is unresolved, not fine. Green: the rule is met with the values at hand. Green is a statement about exactly one rule and exactly the values entered — never about the dossier and never about the procedure.',
  'explainer.lights.note':
    'Every traffic light carries a symbol and a word, with three different symbols. Print the report in black and white, or read colour poorly, and nothing is lost.',
  'explainer.confidence.title': 'The confidence tag on every card',
  'explainer.confidence.body':
    'It says how an entry came about — not how likely it is to hold. There are exactly three:',
  'explainer.confidence.deterministic':
    'deterministic · high — the numbers come from the form or from the plans, and code compared them against the threshold of the cited provision.',
  'explainer.confidence.retrieval':
    'retrieval · medium — the rule comes from a rulebase pack that is not verified against the primary source at this point. Such entries sit in the indicative block and never carry a traffic light.',
  'explainer.confidence.indicator':
    'Indicator · low — an experience value with no legal consequence, for instance a pointer at objection risk. It sits under “Hinweise” and is not a finding.',
  'explainer.numbers.title': 'The numbers on the cards',
  'explainer.numbers.body':
    'What a card shows in the monospace face is exactly what the deterministic check computed: parameter, input value, operator, threshold. None of it was read back out of the message text, none of it rounded afterwards. The unit comes from the parameter’s own name; where the name carries none, none is shown.',
  'explainer.numbers.example':
    'Example: baumasse_hauptbau_m3 = 1’649.86 m³ ≤ 1’650 m³ means — this input value, this threshold, this comparison.',
  'explainer.skipped.title': 'Why some rules did not run',
  'explainer.skipped.body':
    'A rule runs only when its input values resolve. If a value is missing, if this Gemeinde uses a different regime (Baumassenziffer instead of Ausnützungsziffer), or if the rule is not verified yet, it sits under “Manuell · zu prüfen” — with the reason. Skipped means neither met nor breached. That list is the part you check yourself.',
  'explainer.indicative.title': 'What indicative output is',
  'explainer.indicative.body':
    'Dashed border, grey badge, no traffic-light colour: the rule or the zone is not verified against the BZO yet. Such output is never mixed into the findings and never counts in the traffic lights. It says where to look — it does not state a result.',
  'explainer.nachweise.title': 'Vorbehaltene Nachweise',
  'explainer.nachweise.body':
    'Nachweise that only fall due later — energy, fire safety, sound. They are no reason to hold back the Baueingabe, and they are in the report so they are not missing at the moment they are needed.',
  'explainer.liability.title': 'Where the responsibility sits',
  'explainer.liability.body':
    'The Baueingabe-Check flags findings with their source and §, and states next to them what it did not check. It does not decide and it confirms no completeness. Responsibility for the Baueingabe stays with the Projektverfasser. “No red findings” is likewise not a statement about the dossier, but about the rules this pack carries.',

  // --- M3: the print form --------------------------------------------------
  'print.date': 'Date',
  'print.page': 'Page',
  'print.footnote':
    'Print form of the report. It contains nothing the on-screen report does not show, and no summary judgement.',

  'severity.red': 'Red',
  'severity.amber': 'Amber',
  'severity.green': 'Green',
  'severity.red.meaning': 'rule not met',
  'severity.amber.meaning': 'input value missing or open',
  'severity.green.meaning': 'rule met',

  // --- M2: Anmeldung, Einladung, Workspace, Upload -----------------------
  'auth.title': 'Sign in',
  'auth.lead': 'The Baueingabe-Check works without a password. You request a sign-in link; it works once and expires shortly after.',
  'auth.redeeming': 'Redeeming the sign-in link …',
  'auth.linkFailedTitle': 'This sign-in link no longer works',
  'auth.linkFailedNext': 'Request a new link below. A new link invalidates every older one.',
  'auth.requestTitle': 'Request a sign-in link',
  'auth.emailLabel': 'E-mail address',
  'auth.emailPlaceholder': 'first.last@practice.ch',
  'auth.requestAction': 'Request sign-in link',
  'auth.requesting': 'Requesting …',
  'auth.mailNotice.delivers': 'The sign-in link arrives by e-mail. Check your inbox — and the spam folder if nothing turns up.',
  'auth.mailNotice.logOnly': 'This installation does not send mail. The sign-in link is written to the server log; Velox-KAi hands it to you personally.',
  'auth.signOut': 'Sign out',
  'invite.title': 'Invitation',
  'invite.redeeming': 'Redeeming the invitation …',
  'invite.noToken': 'This invitation link is incomplete — the token is missing. Please open the full link from the message, or ask the sender for a new invitation.',
  'invite.failedTitle': 'This invitation cannot be redeemed',
  'invite.failedNext': 'Please ask the sender for a new invitation. If you already have an account, sign in with a sign-in link instead.',
  'invite.toSignIn': 'Go to sign-in',
  'invite.state.open': 'open',
  'invite.state.accepted': 'redeemed',
  'invite.state.revoked': 'revoked',
  'invite.state.expired': 'expired',
  'invitePanel.title': 'Invitations',
  'invitePanel.lead': 'The link is shown exactly once and cannot be retrieved afterwards — the server stores only its hash.',
  'invitePanel.emailLabel': 'E-mail address',
  'invitePanel.roleLabel': 'Role',
  'invitePanel.roleMember': 'Member',
  'invitePanel.roleAdmin': 'Workspace admin',
  'invitePanel.create': 'Create invitation',
  'invitePanel.linkOnce': 'Invitation link — shown only now',
  'invitePanel.copy': 'Copy',
  'invitePanel.noMail': 'No mail was sent. Please deliver the link personally.',
  'invitePanel.mailSent': 'The invitation was sent by e-mail. The link is still shown here — once only, in case the message does not arrive.',
  'invitePanel.revoke': 'Revoke',
  'invitePanel.empty': 'No invitation created yet.',
  'workspace.switchLabel': 'Workspace',
  'workspace.switchNone': 'Please choose',
  'workspace.indexLink': 'All workspaces',
  'workspace.role': 'Role',
  'workspace.role.founder': 'Founder',
  'workspace.role.admin': 'Admin',
  'workspace.role.member': 'Member',
  'workspace.uploadCta': 'Upload a dossier',
  'workspace.dossiersTitle': 'Dossiers in this workspace',
  'workspace.dossiersLead': 'Uploaded and adopted dossiers. They stay inside this workspace.',
  'workspace.dossiersEmpty': 'No dossier in this workspace yet.',
  'workspace.kindUpload': 'Upload',
  'workspace.kindDemo': 'Demo',
  'workspace.attested': 'attested',
  'workspace.notFound': 'This workspace is not visible to this account.',
  'workspace.retention': 'Retention: {days} days. After that, files, facts and runs are deleted automatically and for good.',
  'workspace.window.title': 'Retention window of this workspace',
  'workspace.window.lead': 'How long dossiers in this workspace are kept. Allowed: {min} to {max} days.',
  'workspace.window.label': 'Window in days',
  'workspace.window.optional': 'Leaving it empty takes the server default.',
  'workspace.window.save': 'Set the window',
  'workspace.window.asymmetry': 'Shortening is retroactive: dossiers already stored are re-dated from their own upload date — one already past the new window falls due immediately. Lengthening applies to future uploads only; a shorter promise once given is never extended.',
  'workspace.window.saved': 'Window set to {days} days.',
  'workspace.window.backups': 'Backups age out independently: real deletion happens later than the window set here.',
  'workspace.window.confirm.title': 'Really shorten the window?',
  'workspace.window.confirm.change': 'From {from} to {to} days.',
  'workspace.window.confirm.due': '{count} of {total} stored dossiers fall due immediately.',
  'workspace.window.confirm.dueNone': 'No stored dossier falls due immediately — nothing disappears now.',
  'workspace.window.confirm.redated': 'Re-dated: {count} of {total} dossiers, computed from their own upload date.',
  'workspace.window.confirm.what': 'A dossier that is due is removed by the next sweep — it runs in the background and waits for no further click. Gone with it: the uploaded PDFs, the extracted facts, and every run with its report.',
  'workspace.window.confirm.unknown': 'The dossier list of this workspace is not loaded right now — how many dossiers this affects cannot be said here.',
  'workspace.window.confirm.cta': 'Shorten the window',
  'workspace.window.confirm.busy': 'Setting …',
  'workspace.mode.label': 'Data mode',
  'workspace.mode.title': 'Data mode of this workspace',
  'workspace.mode.lead': 'The data mode decides which data may enter here, and whether the extraction step may use the language model at all.',
  'workspace.mode.current': 'active',
  'workspace.mode.locked': 'locked',
  'workspace.mode.lockedReason': 'This mode is not enabled on this server and cannot be chosen in the application.',
  'workspace.mode.liveLockedReason': 'Live means real personal data from an ongoing permit case. The mode cannot be chosen in the application — it is enabled on the server, and only once a signed DPA is in place and either a verified CH/EU host is configured or sending the extraction step to the US endpoint has been explicitly accepted. Neither is set on this server.',
  'workspace.mode.unknown': 'unknown',
  'workspace.mode.demo': 'Demo',
  'workspace.mode.anonymised': 'Anonymised',
  'workspace.mode.live': 'Live',
  'workspace.mode.demoHint': 'The built-in synthetic dossiers only.',
  'workspace.mode.anonymisedHint': 'Test dossiers without real personal data — the default for a prospect practice.',
  'workspace.mode.liveHint': 'Real personal data from a live permit case. Files and facts stay on this server; the model step is not enabled here, so runs stay deterministic.',
  'workspace.mode.liveHintUs': 'Real personal data from a live permit case. Files and facts stay on this server; for extraction, form text and rendered plan pages are sent to Anthropic (United States) — founder decision of 20 August 2026, no CH/EU host in use.',
  'workspaceIndex.title': 'Workspaces',
  'workspaceIndex.leadFounder': 'Every practice works in its own workspace: its own dossiers, its own runs, its own audit trail.',
  'workspaceIndex.leadMember': 'You see your practice\'s workspace and nothing else.',
  'workspaceIndex.emptyFounder': 'There is no workspace yet. Create the first one below.',
  'workspaceIndex.emptyMember': 'This account belongs to no workspace. Please contact whoever sent the invitation.',
  'workspaceIndex.createTitle': 'Create a workspace',
  'workspaceIndex.createLead': 'A new workspace starts in data mode «Anonymised».',
  'workspaceIndex.nameLabel': 'Name of the practice',
  'workspaceIndex.create': 'Create',
  'upload.title': 'Upload a dossier',
  'upload.lead': 'Plans, forms and calculations as PDF. The pre-check then runs through the stages the backend reports.',
  'upload.filesTitle': 'Files',
  'upload.caps': 'Up to {files} PDFs, at most {perFile} each and {total} in total. The page cap is {pages} pages and is enforced on the server.',
  'upload.dropHere': 'Drag PDFs here',
  'upload.orChoose': 'Or choose files:',
  'upload.accepted': 'accepted',
  'upload.noFiles': 'No file chosen yet.',
  'upload.summary': '{accepted} accepted, {rejected} rejected, {size} in total.',
  'upload.removeFile': 'Remove {name}',
  'upload.contextTitle': 'Context',
  'upload.contextLead': 'Gemeinde, Bautyp and Verfahren decide which rulebase pack applies.',
  'upload.gemeinde': 'Gemeinde',
  'upload.bautyp': 'Bautyp',
  'upload.verfahren': 'Verfahren',
  'upload.pleaseChoose': 'Please choose',
  'upload.zone': 'Zone (optional)',
  'upload.zonePlaceholder': 'e.g. W2.2 — otherwise read from the Baugesuch',
  'upload.dossierTitle': 'Label (optional)',
  'upload.dossierTitlePlaceholder': 'e.g. Neubau EFH Musterweg',
  'upload.missingGemeinde': 'Your Gemeinde is missing from the list? Then no rulebase pack exists for it yet. Upload the Gemeinde’s BZO: that produces a draft pack, which can then be chosen here.',
  'upload.attestTitle': 'Attestation',
  'upload.attestLead': 'Without this confirmation there is no upload. It is recorded in the audit trail.',
  'upload.attestCheckbox': 'This test dossier contains no real personal data / is anonymised',
  'upload.attestHelp': 'This workspace runs in data mode «{mode}». In Demo and Anonymised, no real personal data belongs in a dossier — the confirmation is required and is recorded in the audit trail.',
  'upload.attestCheckboxLiveUs': 'This dossier contains real personal data. I am entitled to process it here. The files are stored and processed on this server; the extraction step sends form text and rendered plan pages to Anthropic (Anthropic PBC, United States). All calculations and rule checks stay on this server.',
  'upload.attestCheckboxLiveLocal': 'This dossier contains real personal data. I am entitled to process it here. The files are stored and processed on this server only; the model step is not enabled for this workspace, so no content is sent to third parties.',
  'upload.attestHelpLive': 'This workspace runs in data mode «{mode}» — real personal data. The confirmation is recorded in the audit trail with who and when. What happens to the files is stated in the sentence above; the Baueingabe-Check verifies neither your entitlement nor the content.',
  'upload.submit': 'Upload and check',
  'upload.submitting': 'Uploading …',
  'upload.blockedAttest': 'Please confirm the attestation first.',
  'upload.blockedFiles': 'Please choose at least one PDF.',
  'upload.blockedGemeinde': 'Please choose the Gemeinde.',
  'upload.reject.notPdf': 'Not a PDF — the content was checked, not the extension.',
  'upload.reject.tooLarge': 'Too large — at most {max} per file.',
  'upload.reject.totalTooLarge': 'Total size exceeded — at most {total}.',
  'upload.reject.tooMany': 'Too many files — at most {files}.',
  'upload.reject.duplicate': 'Already chosen (same name, same size).',
  'upload.reject.empty': 'The file is empty.',
  'upload.bautyp.neubau': 'Neubau',
  'upload.bautyp.umbau': 'Umbau',
  'upload.bautyp.anbau': 'Anbau',
  'upload.bautyp.aufstockung': 'Aufstockung',
  'upload.bautyp.abbruch-wiederaufbau': 'Abbruch und Wiederaufbau',
  'upload.bautyp.ersatzneubau': 'Ersatzneubau',
  'upload.verfahren.ordentlich': 'Ordentliches Verfahren',
  'upload.verfahren.vereinfacht': 'Vereinfachtes Verfahren',
  'upload.verfahren.anzeigeverfahren': 'Anzeigeverfahren',
  'run.llmBlocked': 'The language-model extraction step is blocked for this workspace. The run is purely deterministic; fields only the image and text analysis could read stay empty.',
  'run.isRecheck': 'This run is a deterministic re-check after a fact correction — no model call.',
  'run.openParent': 'Open the previous run',
  'report.isRecheck': 'This report comes from a deterministic re-check after a fact correction.',
  'report.openParentReport': 'Report of the previous run',

  // --- M2: facts review (the trust surface) -------------------------------
  'facts.cta': 'Review facts',
  'facts.title': 'Facts review',
  'facts.lead':
    'Every value the checks reason with — with its provenance and the confidence of its source. Correct what is wrong: the deterministic checks run again immediately, with no model call, and the comparison shows which findings moved as a result.',
  'facts.openReport': 'Open the report',
  'facts.recheckNow': 'Re-check now',
  'facts.llmStatus': 'Extraction',
  'facts.noFactsYet':
    'No facts exist for this dossier yet. The check run extracts them; after that they can be reviewed and corrected here.',
  'facts.empty': 'empty',
  'facts.valueMissing': 'Input value missing',
  'facts.edit': 'Change',
  'facts.enter': 'Enter a value',
  'facts.save': 'Save and re-check',
  'facts.clear': 'clear',
  'facts.editHelp': 'Leaving it empty means: no value. Only the type is checked here; whether a value satisfies a rule is the engine’s decision.',
  'facts.listHelp': 'Short names, separated by commas.',
  'facts.listPlaceholder': 'e.g. baugesuch, situationsplan',
  'facts.notEditable': 'Not editable in the facts review — this field identifies a property or a person and stays out of the audit trail.',
  'facts.notEditableGis': 'Not editable in the facts review — this value comes from the GIS query for the parcel, not from the dossier.',
  'facts.saving': 'Saving the correction …',
  'facts.rechecking': 'Running the deterministic re-check …',
  'facts.recheckTitle': 'Re-check',
  'facts.recheckMeta': 'Deterministically re-checked in {ms} ms — with no model call.',
  'facts.confidence': 'Confidence',
  'facts.prov.deterministisch': 'deterministic',
  'facts.prov.deterministisch.meaning': 'Read from the form or the plan text, or computed from it.',
  'facts.prov.llm': 'LLM',
  'facts.prov.llm.meaning': 'Read off the drawings by the language/vision model — to be checked by a human.',
  'facts.prov.gis': 'GIS',
  'facts.prov.gis.meaning': 'From a GIS query for this parcel.',
  'facts.prov.manuell': 'manual',
  'facts.prov.manuell.meaning': 'Entered or corrected by a person in the facts review.',
  'facts.prov.unbekannt': 'unknown',
  'facts.prov.unbekannt.meaning': 'No provenance arrived with this value. It is not guessed.',
  'facts.missingTitle': 'Missing inputs',
  'facts.missingLead':
    'Values that could not be read reliably. They are deliberately empty rather than guessed. Each entry names the rules that could not be computed because of it.',
  'facts.missingEmpty': 'No missing inputs — every required value is present.',
  'facts.missingNoneBlocking': 'No missing value is currently blocking a rule.',
  'facts.missingRest': '{count} more missing values that block no rule',
  'facts.missingRestOne': '1 more missing value that blocks no rule',
  'facts.missingRestLead':
    'These values are missing too, but they hold up no check. Adding them pays off as soon as a rule needs them.',
  'facts.blocks': 'Blocks the rules',
  'facts.blocksNone': 'Currently blocks no rule of this rulebase pack.',
  'facts.jumpToField': 'Go to the field',
  'facts.group.stammdaten': 'Master data',
  'facts.group.values': 'Values and key figures',
  'facts.group.form': 'Form fields (eBaugesuch)',
  'facts.group.flags': 'Characteristics',
  'facts.group.gis': 'GIS query',
  'facts.group.dokumente': 'Documents recognised',
  'facts.group.nachweise': 'Nachweise',
  'facts.filesTitle': 'Basis: uploaded files',
  'facts.pages': 'pages',
  'facts.kind.vector': 'Vector (CAD export, text readable)',
  'facts.kind.raster': 'Scan (image only)',
  'facts.kind.mixed': 'mixed',
  'facts.kind.unknown': 'kind unknown',

  // --- M2: run notices, diff, internal cost -------------------------------
  'notices.title': 'Notices about this run',
  'notices.runTitle': 'Notices about this run',
  'notices.reportTitle': 'How this run came about',
  'notices.recheckTitle': 'Notices about the re-check',
  'notices.stage': 'Stage',
  'diff.title': 'Comparison',
  'diff.sectionTitle': 'What changed against the previous run',
  'diff.sectionLead': 'Compared by rule ID. The fact lines come from the audit trail.',
  'diff.factsTitle': 'Corrected facts',
  'diff.findingsTitle': 'Findings that moved',
  'diff.change.neu': 'new',
  'diff.change.entfallen': 'gone',
  'diff.change.schwere': 'severity changed',
  'diff.change.wert': 'value changed',
  'diff.wasAbsent': 'not present before',
  'diff.isGone': 'no longer present',
  'diff.moved': 'changed',
  'diff.unchanged': '{count} finding(s) unchanged.',
  'cost.title': 'Token cost (internal)',
  'cost.input': 'Input',
  'cost.output': 'Output',
  'cost.calls': 'Model calls',
  'cost.budget': 'Budget',
  'cost.exceeded': 'Budget exceeded — the run failed because of it.',

  // --- M4: rulebase manager, BZO ingestion, verification --------------------
  'nav.rulebase': 'Rulebase',

  'rulebase.title': 'Gemeinden & rulebase',
  'rulebase.lead':
    'Every pack with its verification status. Verified means a human checked the value against the cited BZO passage. Everything else is a draft and runs in indicative mode.',
  'rulebase.summary': '{total} Gemeinden · {verified} verified · {drafts} drafts.',
  'rulebase.summaryOne': '{total} Gemeinden · {verified} verified · 1 draft.',
  'rulebase.empty': 'No rulebase pack found.',
  'rulebase.draftBadge': 'Draft · not verified',
  'rulebase.draftNote': 'Draft — not yet verified against the BZO.',
  'rulebase.verifiedBadge': 'verified',
  'rulebase.verifiedBadgeCount': 'verified · {verified}/{total} rules',
  'rulebase.openCountBadge': '{count} open',
  'packDetail.lead':
    'This pack is committed and lives in the rulebase repository. The page is read-only: it shows every zone row with its parameters and every rule a run for this Gemeinde loads — with status, source and what it checks.',
  'packDetail.coverageTitle': 'Verification status',
  'packDetail.hoehenregime': 'Height law',
  'packDetail.bzoVersion': 'Edition',
  'packDetail.loadError': 'The rule detail of this pack could not be loaded',
  'packDetail.zonesTitle': 'Zones',
  'packDetail.zonesLead':
    'Every row of the zone matrix as the engine reads it. Only a verified row can carry a hard finding; an open row computes indicatively.',
  'packDetail.zonesEmpty': 'The server reports no zone rows for this pack.',
  'packDetail.zonesCaption': 'Zone matrix {gemeinde}: {count} rows',
  'packDetail.zoneCode': 'Zone code',
  'packDetail.zoneStatus': 'Verification',
  'packDetail.zoneParameters': 'Parameters',
  'packDetail.zoneAliases': 'also: {aliases}',
  'packDetail.zoneRegime': 'Density regime {regime}',
  'packDetail.zoneNoParameters': 'no zone-wide parameters',
  'packDetail.zoneVerifiedFrom': 'Evidence: {source}',
  'packDetail.zoneVerifiedOn': 'verified on {date}',
  'packDetail.zoneHeldBack': 'Held back, not for the engine: {values}',
  'packDetail.zoneNull': 'no value',
  'packDetail.layersTitle': 'Rules by layer',
  'packDetail.layersLead':
    'A run for this Gemeinde loads federal, cantonal and municipal rules on top of each other. Each layer lists its rules below, in the order the engine loads them.',
  'packDetail.layersEmpty': 'The server reports no layers for this pack.',
  'packDetail.layerRules': '{count} rules',
  'packDetail.layerRulesOne': '1 rule',
  'packDetail.ruleType': 'Type',
  'packDetail.ruleType.computable': 'computable',
  'packDetail.ruleType.required': 'required document',
  'packDetail.ruleType.nachweis': 'proof (Nachweis)',
  'packDetail.ruleType.narrative': 'judgement',
  'packDetail.ruleChecks': 'Checks',
  'packDetail.ruleNoCheck': 'Judgement rule — no comparison; the report classifies it, with lower confidence.',
  'packDetail.ruleSeverity': 'On failure',
  'packDetail.ruleInputs': 'Inputs',
  'packDetail.ruleAppliesTo': 'Applies to',
  'packDetail.ruleVerifyNote': 'Still to verify',
  'packDetail.ruleVorbehalt': 'Proviso',
  'packDetail.ruleUnverified':
    'Not verified — this rule never produces a hard finding, only a note «rule not yet verified».',
  'packDetail.ruleIdsNote':
    'The rules above are exactly the ones a run for this Gemeinde loads. Nothing can be changed here: a committed pack changes only through a reviewed diff to rulebase/{pack}/rules.yaml and zones.yaml in the repository.',
  'rulebase.draftSectionTitle': 'Drafts — waiting for verification',
  'rulebase.verifiedSectionTitle': 'Verified packs',
  'rulebase.zones': 'Zone rows',
  'rulebase.zoneParameters': 'Zone parameters',
  'rulebase.rules': 'Rules in effect',
  'rulebase.verified': 'verified',
  'rulebase.open': 'open',
  'rulebase.sources': 'Source documents',
  'rulebase.noSources': 'No source document on record.',
  'rulebase.pages': '{count} pages',
  'rulebase.ingestedAt': 'ingested on {date}',
  'rulebase.openPack': 'Open and verify',
  'rulebase.openPackVerified': 'View pack',
  'rulebase.noPackFor': 'A Gemeinde missing? Its BZO can be ingested here.',

  'rulebase.ingestCta': 'Ingest a BZO',
  'rulebase.ingestTitle': 'Ingest a BZO',
  'rulebase.ingestLead':
    'For a Gemeinde with no pack: the public BZO is read and turned into a draft pack. The draft holds data with a source snippet and a page reference only — no formula, and nothing verified.',
  'rulebase.ingestGemeinde': 'Gemeinde',
  'rulebase.ingestGemeindePlaceholder': 'e.g. Zollikon',
  'rulebase.ingestKanton': 'Canton',
  'rulebase.ingestFileTitle': 'BZO document',
  'rulebase.ingestDropHere': 'Drop the BZO PDF here',
  'rulebase.ingestOrChoose': 'or choose a file',
  'rulebase.ingestNoFile': 'No file chosen yet.',
  'rulebase.ingestNotPdf': 'Not a PDF — the content was checked, not the extension.',
  'rulebase.ingestFileDescription': 'One PDF with a text layer, at most {max}.',
  'rulebase.ingestTooLarge': 'Larger than {max}.',
  'rulebase.ingestSubmit': 'Start the ingestion',
  'rulebase.ingestSubmitting': 'Starting …',
  'rulebase.ingestBlockedFile': 'Please choose a BZO PDF first.',
  'rulebase.ingestBlockedGemeinde': 'Please enter the Gemeinde first.',
  'rulebase.ingestReplaceTitle': 'There is already a draft for this Gemeinde.',
  'rulebase.ingestReplace': 'Replace the existing draft',
  'rulebase.ingestScopeNote':
    'The draft belongs to this workspace. Another Büro does not see it.',

  'rulebase.ingestAttestTitle': 'Attestation',
  'rulebase.ingestAttestCheckbox': 'This is a publicly available document of the Gemeinde (Bau- und Zonenordnung) and contains no personal data.',
  'rulebase.ingestBlockedAttest': 'Please tick the attestation first.',
  'rulebase.ingestionTitle': 'BZO ingestion',
  'rulebase.ingestionLead': 'The stages below are the stages the backend actually reports.',
  'rulebase.ingestionStagesTitle': 'Stages',
  'rulebase.ingestionFailed': 'The ingestion failed',
  'rulebase.ingestionPollError': 'The status could not be fetched.',
  'rulebase.ingestionOpenPack': 'Open the draft pack',
  'rulebase.ingestionGemeinde': 'Gemeinde',
  'rulebase.shadowedSectionTitle': 'Drafts that are not used',
  'rulebase.shadowedTitle': 'This draft is not used.',
  'rulebase.shadowedLead':
    'A verified pack for this Gemeinde already exists in the rulebase. The draft stays readable, but no check run touches it.',
  'rulebase.conflicts': 'Affected',

  'verify.title': 'Verification against the BZO',
  'verify.lead':
    'On the left is what was read out of the document. On the right is the passage it came from. Confirm only what you can actually see there — and correct the rest; both cost the same.',
  'verify.packNotFound': 'There is no pack at this address.',
  'verify.exportCta': 'Export as YAML',
  'verify.exportHint':
    'The export is the way into the repository: the files are committed through the ordinary git flow, so the diff stays readable.',
  'verify.zonesTitle': 'Zone parameters',
  'verify.zonesLead':
    'One row is one row of the BZO zone table. Bulk confirmation exists at exactly this level — never for a whole pack.',
  'verify.rulesTitle': 'Candidate rules',
  'verify.rulesLead':
    'The draft deliberately holds no formula. A computable comparison comes into being when a human enters the operator and the value from the cited passage.',
  'verify.zonesEmpty': 'The draft holds no zone rows.',
  'verify.rulesEmpty': 'The draft holds no candidate rules.',
  'verify.noWholePack':
    'There is no control that confirms a whole pack — neither here nor in the backend. Verification happens per zone row and per rule, each time with its citation.',
  'verify.founderOnlyNote':
    'Verification against the primary source is done by Velox-KAi. Your draft is kept and is checked as "indicative". You can read every row and its source here.',
  'verify.founderOnlyRow': 'Confirming and correcting is reserved for Velox-KAi.',

  'verify.extracted': 'Value read',
  'verify.sourceTitle': 'Source',
  'verify.page': 'Page {page}',
  'verify.pageUnknown': 'Page unknown',
  'verify.openSource': 'Open the page in the PDF',
  'verify.noSourceLink':
    'The source PDF is not served for this pack. Check the snippet against your own copy.',
  'verify.noCitation':
    'There is no source snippet for this entry. Without a citation it cannot be confirmed here.',
  'verify.sameSource': 'Same passage as above.',
  'verify.expand': 'Open the row and check it against the source',
  'verify.collapse': 'Close the row',
  'verify.zoneRowItem': 'The zone row itself',
  'verify.zoneRowItemHint':
    'Confirms that this zone stands in the BZO as shown. Only this takes the Gemeinde out of draft mode.',
  'verify.disposition': 'Against the source',
  'verify.evidenceFor': 'Evidence for',
  'verify.confirm': 'matches',
  'verify.correct': 'correct it',
  'verify.unreadable': 'not readable',
  'verify.unreadableHint': 'Recorded as a missing input value — not as 0.',
  'verify.newValue': 'Value per the BZO',
  'verify.newValuePlaceholder': 'Enter the value',
  'verify.notRead': 'not read',
  'verify.ratioBoth': 'In the document {printed} — normalised {normalised}',
  'verify.ratioOpen':
    'Notation open: {printed} reads as a percentage and as a fraction. Please enter the value as the document prints it.',
  'verify.readAs': 'In the document as {term}',
  'verify.markAllConfirmed': 'Mark the whole row as «matches»',
  'verify.markAllHint':
    'Sets every entry in this row to «matches». You can still correct individual entries afterwards.',
  'verify.submitRow': 'Commit {count} entries',
  'verify.submitRowOne': 'Commit 1 entry',
  'verify.submitRowNone': 'Nothing decided yet',
  'verify.submitting': 'Committing …',
  'verify.rowSaved': 'Committed and recorded with its citation.',
  'verify.rowError': 'The commit failed',
  'verify.correctionNeedsValue': 'Please enter the value per the BZO.',
  'verify.status.verified': 'verified',
  'verify.status.unverified': 'open',
  'verify.status.placeholder': 'placeholder',
  'verify.progress': '{verified} of {total} verified',
  'verify.ruleKind': 'Rule type',
  'verify.ruleKindUnknown': 'not determined yet',
  'verify.ruleCheckTitle': 'Comparison',
  'verify.ruleNoCheck': 'The draft holds no comparison — by design, not by omission.',
  'verify.ruleOp': 'Operator',
  'verify.ruleValue': 'Limit',
  'verify.ruleValuePlaceholder': 'Number from the cited passage',
  'verify.ruleArtifact': 'Required document',
  'verify.ruleReject': 'discard',
  'verify.ruleRejectHint': 'The extraction found something here that is not a rule.',
  'verify.ruleSubmit': 'Commit the rule',
  'verify.ruleNeedsCheck': 'Please enter the operator and the limit from the source.',

  // ── M5 ────────────────────────────────────────────────────────────────
  'retention.title': 'Retention',
  'retention.window': 'Retention: {days} days',
  'retention.note': 'Once the window closes, the Baueingabe-Check deletes the uploaded files, the extracted facts and every run with its report — automatically and for good. The audit trail keeps the record that a deletion happened; the deleted values do not stay.',
  'retention.deleteOn': 'Deletion on {date}',
  'retention.deleteIn': 'Deletion in {days} days (on {date})',
  'retention.deleteToday': 'Deletion today',
  'retention.due': 'Window has closed — the next sweep removes it',
  'retention.none': 'No window recorded',
  'delete.cta': 'Delete now',
  'delete.title': 'Delete this dossier for good?',
  'delete.what': 'This removes the uploaded PDFs on the server, the extracted facts, and every run with its report.',
  'delete.irreversible': 'This cannot be undone.',
  'delete.keeps': 'The audit trail keeps the fact that a deletion happened — who, when and how much. The deleted values themselves do not stay.',
  'delete.confirm': 'Delete permanently',
  'delete.cancel': 'Cancel',
  'delete.busy': 'Deleting …',
  'delete.done': 'Deleted: {files} file(s), {runs} run(s). {redacted} audit entries remain, without their content.',
  'dossierAudit.cta': 'History',
  'dossierAudit.title': 'History of this dossier',
  'dossierAudit.lead': 'Uploads, runs, fact corrections, exports and data-mode changes — who, when, what.',
  'dossierAudit.back': 'Back to the workspace',
  'audit.when': 'Time',
  'audit.who': 'Who',
  'audit.what': 'Event',
  'audit.detail': 'Details',
  'audit.empty': 'No events recorded yet.',
  'audit.system': 'system',
  'audit.redacted': 'Content removed when the retention window closed',
  'audit.action.auth.sign_in': 'Signed in',
  'audit.action.auth.sign_out': 'Signed out',
  'audit.action.invite.created': 'Invite created',
  'audit.action.invite.revoked': 'Invite revoked',
  'audit.action.invite.accepted': 'Invite redeemed',
  'audit.action.workspace.created': 'Workspace created',
  'audit.action.workspace.data_mode_changed': 'Data mode changed',
  'audit.action.workspace.deleted': 'Workspace deleted',
  'audit.action.dossier.uploaded': 'Dossier uploaded',
  'audit.action.dossier.fact_edited': 'Facts corrected',
  'audit.action.dossier.deleted': 'Dossier deleted',
  'audit.action.run.started': 'Run started',
  'audit.action.run.finished': 'Run finished',
  'audit.action.report.exported': 'Report exported',
  'audit.action.rulebase.bzo_ingested': 'BZO ingested',
  'audit.action.rulebase.zone_verified': 'Zone row verified',
  'audit.action.rulebase.rule_verified': 'Rule verified',
  'audit.action.rulebase.pack_exported': 'Pack exported',
  'audit.action.retention.swept': 'Retention sweep run',
  'nav.admin': 'Administration',
  'admin.title': 'Founder overview',
  'admin.lead': 'Every workspace, every run, the token cost and the verification work still open. This view is Velox-internal.',
  'admin.forbidden': 'This view is reserved for the founder role.',
  'admin.workspacesTitle': 'Workspaces',
  'admin.workspacesCaption': 'One practice per row, with counters — no content from any dossier.',
  'admin.col.workspace': 'Workspace',
  'admin.col.dataMode': 'Data mode',
  'admin.col.members': 'Members',
  'admin.col.dossiers': 'Dossiers',
  'admin.col.runs': 'Runs',
  'admin.col.failed': 'of those failed',
  'admin.col.files': 'Files',
  'admin.col.storage': 'On disk',
  'admin.col.drafts': 'Draft packs',
  'admin.col.lastRun': 'Last run',
  'admin.col.tokens': 'Tokens (in/out)',
  'admin.col.tokensDay': 'Tokens 24 h / day ceiling',
  'admin.col.retention': 'Retention',
  'admin.col.due': 'due',
  'admin.col.status': 'State',
  'admin.col.mode': 'Kind',
  'admin.col.duration': 'Duration',
  'admin.col.started': 'Started',
  'admin.col.gemeinde': 'Gemeinde',
  'admin.col.open': 'Open',
  'admin.totals': 'Total',
  'admin.dayBudgetReached': 'Day ceiling reached',
  'admin.dayBudgetLead': 'The day ceiling applies per workspace over a rolling 24 hours (BAUPILOT_TOKEN_BUDGET_PER_WORKSPACE_DAY). A dash means no ceiling is set. The totals row has no ceiling of its own.',
  'admin.runsTitle': 'Runs across all workspaces',
  'admin.runsCaption': 'The most recent runs with their token cost. The cost figure is internal.',
  'admin.runsEmpty': 'No run yet.',
  'admin.openRun': 'Open run',
  'admin.queueTitle': 'Verification queue',
  'admin.queueLead': 'Draft packs from every workspace that nobody has confirmed against the source yet. Until then they run in indicative mode.',
  'admin.queueEmpty': 'No draft pack is waiting for verification.',
  'admin.queueOpen': '{zones} zone row(s), {rules} rule(s) open',
  'admin.queueRefused': 'Refused by the data-only gate — this draft is not used.',
  'admin.queueVerify': 'Verify',
  'admin.systemTitle': 'System-wide events',
  'admin.systemLead': 'Events that belong to no workspace: founder sign-ins and retention sweeps.',
  'admin.retentionTitle': 'Retention and the deletion sweep',
  'admin.sweepEvery': 'In this process the sweep runs every {minutes} minutes.',
  'admin.sweepOff': 'The scheduled sweep is switched off (interval 0). Nothing is deleted unless someone presses "Delete now" here or a cron starts the sweep.',
  'admin.lastSweep': 'Last sweep in this process: {when} — {count} dossier(s) deleted.',
  'admin.neverSwept': 'No sweep has run in this process yet.',
  'admin.dueNow': '{count} dossier(s) are due.',
  'admin.dueNone': 'Nothing is due right now.',
  'admin.sweepDry': 'Count only',
  'admin.sweepRun': 'Run the sweep now',
  'admin.sweepBusy': 'The sweep is running …',
  'admin.sweepDone': 'Sweep finished: {dossiers} dossier(s), {files} file(s), {runs} run(s) deleted.',
  'admin.sweepDryDone': 'Count: {due} dossier(s) would be due now. Nothing was deleted.',
  'admin.deleteWorkspaceTitle': 'Delete a workspace',
  'admin.deleteWorkspaceWhat': 'This removes every dossier with its files, every run and report, the draft packs, the invites, the memberships and the audit trail of this workspace.',
  'admin.deleteWorkspaceKeeps': 'What remains is a single entry with no workspace attached: that this workspace was deleted, when, and how much went with it.',
  'admin.deleteWorkspaceLabel': 'Type the short name "{slug}" to confirm',
  'admin.deleteWorkspaceCta': 'Delete this workspace for good',
  'admin.deleteWorkspaceMismatch': 'The short name does not match — nothing was deleted.',
  'admin.deleteWorkspaceDone': 'The workspace is gone: {dossiers} dossier(s), {files} file(s), {runs} run(s), {audit} audit entries.',
  'admin.selectWorkspace': 'Choose a workspace',

  'notFound.title': 'Page not found',
  'notFound.lead': 'This address does not belong to any Baueingabe-Check-KAi view.',
  'notFound.home': 'Go to the overview',
}
